// owl custom element for initilization
// Guard the whole definition: this file can be loaded more than once (theme.liquid
// + the cc-slideshow inline loader), and a second top-level `class` declaration
// throws "already declared". Skip it entirely if the element is already registered.
if (!customElements.get("custom-owlcarousel")) {
class OwlCarouselElement extends HTMLElement {
  constructor() {
    super();
    this.owlInstance = null;
    this.resizeHandler = null;
  }

  connectedCallback() {
    this.originalItems = this.getCarousel()?.children.length || 0;

    // Lazy-init: Owl is a jQuery plugin and initialising is expensive, so only
    // spin it up when the carousel nears the viewport. Above-the-fold carousels
    // (in view at load) init immediately; below-fold ones defer until scrolled
    // near — spreading JS execution time off the initial load WITHOUT gating on
    // user interaction, so buttons stay responsive.
    if ("IntersectionObserver" in window) {
      this.io = new IntersectionObserver(
        (entries, observer) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.io = null;
            this.init();
            this.setupResizeListener();
          }
        },
        { rootMargin: "200px 0px" }
      );
      this.io.observe(this);
    } else {
      this.init();
      this.setupResizeListener();
    }
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.destroyOwl();
  }

  setupResizeListener() {
    this.resizeHandler = () => this.updateOwlState();
    window.addEventListener("resize", this.resizeHandler);
  }

  init() {
    this.updateOwlState();
  }

  getCarousel() {
    return this.querySelector(".owl-carousel");
  }

  getResponsiveConfig() {
    const attr = this.getAttribute("data-responsive");
    if (!attr) return {};
    try {
      return JSON.parse(attr);
    } catch (e) {
      console.warn("Invalid data-responsive JSON", e);
      return {};
    }
  }

  getCurrentItemsCount() {
    const responsive = this.getResponsiveConfig();
    const width = document.documentElement.clientWidth;
    let items = 1;

    Object.keys(responsive).forEach(bp => {
      if (width >= Number(bp) && responsive[bp].items) {
        items = responsive[bp].items;       
      }
    });
    
    return items;
  }

  updateOwlState() {
    const carousel = this.getCarousel();
    if (!carousel) return;

    const totalItems = this.originalItems;
    const visibleItems = this.getCurrentItemsCount();

    if (totalItems > visibleItems) {
      if (!this.owlInstance) this.enableOwl();
    } else {
      if (this.owlInstance) this.destroyOwl();
    }
  }

 enableOwl() {
  if (this.owlInstance) return;

  const $carousel = $(this.getCarousel());

  const loopEnabled = this.getAttribute("data-loop") === "true";
  const autoplayEnabled = this.getAttribute("data-autoplay") === "true";

  // ✅ SINGLE DECLARATION ONLY
  const options = {
    loop: loopEnabled,
    rewind: !loopEnabled && this.getAttribute("data-rewind") === "true",
    dots: this.getAttribute("data-dots") === "true",
    nav: this.getAttribute("data-nav") === "true",
    autoplay: autoplayEnabled,
    autoWidth: false,
    center: this.getAttribute("data-center") === "true",

    autoplayTimeout: Number(this.getAttribute("data-autoplay-timeout") || 6000),

    responsive: this.getResponsiveConfig()
  };

  // optional extensions (NO re-declaration)
  if (this.hasAttribute("data-autoplay-speed")) {
    options.autoplaySpeed = Number(this.getAttribute("data-autoplay-speed"));
  }

  if (this.hasAttribute("data-smart-speed")) {
    options.smartSpeed = Number(this.getAttribute("data-smart-speed"));
  }

  if (this.hasAttribute("data-slide-transition")) {
    options.slideTransition = this.getAttribute("data-slide-transition");
  }

  if (this.hasAttribute("data-animate-in")) {
    options.animateIn = this.getAttribute("data-animate-in");
  }

  if (this.hasAttribute("data-animate-out")) {
    options.animateOut = this.getAttribute("data-animate-out");
  }

  if (this.hasAttribute("data-mouse-drag")) {
    options.mouseDrag = this.getAttribute("data-mouse-drag") === "true";
  }

  if (this.hasAttribute("data-touch-drag")) {
    options.touchDrag = this.getAttribute("data-touch-drag") === "true";
  }

  // Set the instance ref up-front so a rapid resize can't trigger a double init.
  this.owlInstance = $carousel;

  // A11y: owl marks its nav buttons role="presentation", which is invalid on an
  // interactive <button>. Bind BEFORE init so we catch owl's "initialized" event,
  // then drop the role and give the buttons real labels.
  $carousel.on("initialized.owl.carousel refreshed.owl.carousel", function () {
    $carousel.find(".owl-nav button").each(function () {
      this.removeAttribute("role");
      if (!this.getAttribute("aria-label")) {
        this.setAttribute(
          "aria-label",
          this.classList.contains("owl-prev") ? "Precedente" : "Successivo"
        );
      }
    });
  });

  // Initialise owl on the next animation frame. Owl reads element geometry to lay
  // out the slides; running it at a frame boundary lets the browser do that layout
  // naturally instead of forcing a synchronous reflow in the middle of a JS task.
  requestAnimationFrame(function () {
    $carousel.owlCarousel(options);
  });
}

  setupResizeListener() {
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateOwlState();
      }, 200);
    };
    window.addEventListener("resize", this.resizeHandler);
  }
  
  destroyOwl() {
    if (!this.owlInstance) return;

    this.owlInstance.trigger("destroy.owl.carousel");
    this.owlInstance.find(".owl-stage-outer").children().unwrap();
    this.owlInstance.removeClass("owl-loaded");
    this.owlInstance = null;
  }
}

  customElements.define("custom-owlcarousel", OwlCarouselElement);
}
